import { CookieServiceOptions } from '@oneaudi/audi-cookie-service';

import {
  INTEGRATOR_ID,
  createFallbackConsentManager,
  getSysEnv,
} from '@oneaudi/integrator-shared';

import {
  getMetaContent,
  getPreviewToken,
  getPrimaryConsumerId,
} from './page-content';

import type { AudiMarketContextServiceOptions } from '@oneaudi/market-context-service';
import type { AuthServiceOptions } from '@oneaudi/audi-auth-service';
import type { CurrencyFormatterConfig } from '@oneaudi/number-formatter-service';
import type { CurrentCarlineServiceConfig } from '@oneaudi/current-carline-service';
import type { DisclaimerManagerOptions } from '@volkswagen-onehub/disclaimer-manager';
import type { EnvConfigServiceOptions } from '@oneaudi/audi-env-config-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { I18NServiceOptions } from '@oneaudi/i18n-service';
import type { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';
import type { NavigationServiceOptions } from '@volkswagen-onehub/navigation-service';
import type { OneGraphServiceOptions } from '@oneaudi/onegraph-service';
import { RenderMode } from '@oneaudi/render-mode-service';
import type { RootLocationTransformer } from '@oneaudi/feature-services';
import type { TrackingServiceOptions } from '@oneaudi/audi-tracking-service';
import type { VueFormatterServiceOptions } from '@oneaudi/vue-formatter-service';

import { createRootLocationTransformer } from '@oneaudi/feature-services';

import { createLogger } from '../create-logger';
import { removePrivateData } from './tracking-url-sanitizer';

const logger = createLogger();

export const getDefaultLogoutRedirectUrl = (dom: Element): string => {
  const logoutUrl = getMetaContent(dom, 'myaudi-logout-url');
  if (!logoutUrl) {
    return location.href;
  }

  return logoutUrl;
};

export const getApiGatewayUrl = (dom: Element): string =>
  getMetaContent(dom, 'wag-baseurl');

export const getAuthProxyUrl = (dom: Element): string => {
  const myAudiAuthProxy = getMetaContent(dom, 'myaudi-auth-proxy');
  if (myAudiAuthProxy) {
    return myAudiAuthProxy;
  }

  logger.error('No AuthProxy URL configured in meta tag "myaudi-auth-proxy"');
};

export const getAuth0UiLocale = (dom: Element): string => {
  return getMetaContent(dom, 'auth0UiLocale');
};

export const getAuthOptions = (dom: Element): AuthServiceOptions => {
  return {
    authProxyUrl: getAuthProxyUrl(dom),
    defaultLoginRedirectUrl: location.href,
    defaultLogoutRedirectUrl: getDefaultLogoutRedirectUrl(dom),
    fagScopeMappings: new Map<string, string[]>([['myaudi', []]]),
    auth0UiLocale: getAuth0UiLocale(dom) || undefined,
  };
};

export const getDpuApiGatewayUrl = (dom: Element): string => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return `${apiGateWayUrl}/dpu/v1/${country}/${language}`;
};

export const getMarketContextApiGatewayUrl = (dom: Element): string => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return `${apiGateWayUrl}/market-context/v1/${country}/${language}`;
};

export const getOneGraphOptions = (dom: Element): OneGraphServiceOptions => {
  const dpuApiGatewayUrl = getDpuApiGatewayUrl(dom);
  const oneGraphOptions = {
    enableBatman: getMetaContent(dom, 'bat-signal') === 'true',
    oneGraphDataVersionsUrl: `${dpuApiGatewayUrl}/versions`,
    oneGraphUrl: getMetaContent(dom, 'cola-api-url'),
    ...getPreviewJwtTokenOption(dom),
  };

  return oneGraphOptions;
};

export const getPreviewJwtTokenOption = (
  dom: Element,
): OneGraphServiceOptions => {
  const jwtToken = getPreviewToken(dom);
  if (jwtToken) {
    return { preview: { jwtToken: jwtToken } };
  }

  return {};
};

export const getCurrentCarlineOptions = (
  dom: Element,
): CurrentCarlineServiceConfig => {
  return {
    carline: getMetaContent(dom, 'carline'),
    carlineGroup: getMetaContent(dom, 'carlinegroup'),
    prstring: getMetaContent(dom, 'prstring'),
  };
};

export const getEnvConfigOptions = (dom: Element): EnvConfigServiceOptions => {
  const sysEnv = getSysEnv(getApiGatewayUrl(dom));
  const envConfigBaseUrl = 'https://env-config.one.audi';

  // country specific cases
  const localeServiceDefinition = getLocaleOptions(dom);

  let templateUrl = `${envConfigBaseUrl}/config/${sysEnv}/{key}.json`;
  // us / AoA?
  if (localeServiceDefinition.countryCode.toLowerCase() === 'us') {
    templateUrl = `${envConfigBaseUrl}/config/aoa/${sysEnv}/{key}.json`;
  }

  return {
    templateUrl,
  };
};

const gfaServicesDefaultConfigurationOneshopBffPart = {
  id: '?',
  origin: window.location.origin,
  path: '/',
};

const gfaServicesDefaultConfigurationOneshopUbffPart = {
  id: '?',
  origin: window.location.origin,
  path: '/',
};

export const gfaServicesDefaultConfiguration = {
  oneshopBff: { ...gfaServicesDefaultConfigurationOneshopBffPart },
  oneshopUbff: { ...gfaServicesDefaultConfigurationOneshopUbffPart },
};

export const getGfaServiceConfig = (): GfaServiceConfigProviderV1 => {
  return { gfa: { ...gfaServicesDefaultConfiguration } };
};

export const getDisclaimerManagerOptions = (): DisclaimerManagerOptions => {
  return {
    defaultLegalEntity: 'audi',
    legalEntityLabels: { audi: 'Audi' },
  };
};

export const getI18nOptions = (dom: Element): I18NServiceOptions => {
  return {
    i18nEndpointUrl: getMetaContent(dom, 'i18n-endpoint'),
  };
};

export const getLocaleOptions = (dom: Element): LocaleServiceConfig => {
  return {
    brand: 'A',
    countryCode: getMetaContent(dom, 'locale-market') || 'DE',
    currency: getMetaContent(dom, 'locale-currency') || 'EUR',
    language: getMetaContent(dom, 'locale-language') || 'de',
    market: getMetaContent(dom, 'locale-iso-3166-1-alpha3') || 'DEU',
  };
};

export const getMarketContextServiceOptions = (
  dom: Element,
): AudiMarketContextServiceOptions => {
  return {
    v2: {
      apiGatewayUrl: getMarketContextApiGatewayUrl(dom),
    },
  };
};

export const getNavigationOptions: () => NavigationServiceOptions = () => {
  // respect overriden navigation service options
  const navigationOptions =
    window.featureServiceConfigs?.['navigation-service'];
  if (Array.isArray(navigationOptions) && navigationOptions.length == 1) {
    return navigationOptions[0];
  }

  return {
    pages: {},
    primaryConsumerId: INTEGRATOR_ID,
    primaryConsumerLocation: (): object => {
      return {};
    },
  };
};

export const getNumberFormatterOptions = (
  dom: Element,
): CurrencyFormatterConfig => {
  return {
    countryCode: getMetaContent(dom, 'locale-market'),
    currency: getMetaContent(dom, 'locale-currencyType') as
      | 'code'
      | 'narrowSymbol',
    currencyISOCode: getMetaContent(dom, 'locale-currencyISOCode'),
    language: getMetaContent(dom, 'locale-language'),
    showDecimals: 'true' === getMetaContent(dom, 'locale-showDecimals'),
  };
};

export const getRenderModeOptions = (): RenderMode => {
  const renderMode = document.body.className.includes('cq-wcm-edit')
    ? RenderMode.EDIT
    : RenderMode.DEFAULT;
  return renderMode;
};

export const getTrackingOptions = (
  domBody: Element,
): TrackingServiceOptions => {
  return {
    datalayer: window.digitalData,
    primaryConsumerId: getPrimaryConsumerId(domBody),
    sanitizeUrl: (url: string): string => removePrivateData(url, logger),
  };
};

export const getVueFormatterOptions = (
  dom: Element,
): VueFormatterServiceOptions => {
  let vueAgwEndpoint = getMetaContent(dom, 'vue-agw-endpoint');

  try {
    // test if the url is valid
    const url = new URL(vueAgwEndpoint);
    if (url.host) {
      if (!url.host.endsWith('audi.com')) {
        logger.warn(
          `invalid url for vue agw endpoint detected: ${vueAgwEndpoint}. Will fallback to default endpoint`,
        );
        vueAgwEndpoint = null;
      }
    }
  } catch (error) {
    logger.warn(
      `Error parsing url: ${vueAgwEndpoint}. Will fallback to default endpoint`,
      error,
    );
    vueAgwEndpoint = null;
  }

  // fallback to default url if no valid url is provided
  if (!vueAgwEndpoint) {
    vueAgwEndpoint = 'https://vue-agw.assets.audi.com';
  }

  const vueFormatterOptions: VueFormatterServiceOptions = {
    KNUCKLES_I18N_ENDPOINT:
      getMetaContent(dom, 'bat-signal') === 'true'
        ? 'https://knuckles-i18n.web-api.audi.com/cae-formatter/com'
        : undefined,
    VUE_AGW_ENDPOINT: vueAgwEndpoint,
  };

  return vueFormatterOptions;
};

export const prepareWindow = function (): void {
  window.featureServiceDefinitions = window.featureServiceDefinitions || [];

  // if there is no datalayer we provide one. as convenience for the developer
  if (typeof window.digitalData === 'undefined') {
    window.digitalData = { event: [] };
  }
};

export const getCookieServiceOption = function (): CookieServiceOptions {
  return {
    consentManager: createFallbackConsentManager(logger),
  };
};

export function getHistoryServiceConfig(
  domHead: Element,
  domBody: Element,
): RootLocationTransformer {
  const navigationServiceOptions = getNavigationOptions();
  const primaryConsumerId = getPrimaryConsumerId(domBody);

  return createRootLocationTransformer(
    INTEGRATOR_ID,
    primaryConsumerId,
    navigationServiceOptions,
  );
}
